/**
 * Wires the FAQ sidebar navigation to the heading blocks next to it.
 *
 * - The sidebar lists the section's heading blocks; clicking one scrolls to it.
 * - `data-single-open` closes the other collapsible rows when one is opened.
 * - The link matching the heading nearest the top of the viewport stays highlighted.
 */
class VanityFaq extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {Map<string, HTMLAnchorElement>} */
  #links = new Map();

  /** @type {HTMLElement[]} */
  #anchors = [];

  /** @type {HTMLDetailsElement[]} */
  #items = [];

  /** @type {Set<string>} */
  #visible = new Set();

  get #singleOpen() {
    return this.dataset.singleOpen === 'true';
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.#anchors = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-faq-anchor]')]);
    this.#items = /** @type {HTMLDetailsElement[]} */ ([...this.querySelectorAll('[data-faq-item]')]);

    for (const link of this.querySelectorAll('[data-faq-nav]')) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      const target = link.dataset.faqNav;
      if (!target) continue;

      this.#links.set(target, link);
      link.addEventListener('click', this.#handleNavClick, { signal });
    }

    for (const item of this.#items) {
      item.addEventListener('toggle', this.#handleToggle, { signal });
    }

    this.#observeAnchors();
    this.#scrollFromHash();
    this.#syncActiveLink();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleNavClick = (event) => {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    const id = link.dataset.faqNav;
    if (!id) return;

    const anchor = this.querySelector(`#${CSS.escape(id)}`);
    if (!(anchor instanceof HTMLElement)) return;

    event.preventDefault();

    this.#setActiveLink(id);
    anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * @param {Event} event
   */
  #handleToggle = (event) => {
    const item = event.currentTarget;
    if (!(item instanceof HTMLDetailsElement) || !item.open || !this.#singleOpen) return;

    for (const other of this.#items) {
      if (other !== item) other.open = false;
    }
  };

  #observeAnchors() {
    if (!('IntersectionObserver' in window) || this.#anchors.length === 0) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#visible.add(entry.target.id);
          } else {
            this.#visible.delete(entry.target.id);
          }
        }

        this.#syncActiveLink();
      },
      { rootMargin: '0px 0px -70% 0px', threshold: 0 }
    );

    for (const anchor of this.#anchors) {
      this.#observer.observe(anchor);
    }
  }

  /**
   * Highlights the last heading that has scrolled past the top of the tracking band,
   * falling back to the first heading before any of them is reached.
   */
  #syncActiveLink() {
    if (this.#anchors.length === 0) return;

    const passed = this.#anchors.filter((anchor) => this.#visible.has(anchor.id));
    const active = passed.at(-1) ?? this.#anchors[0];

    if (active) this.#setActiveLink(active.id);
  }

  /**
   * @param {string} id
   */
  #setActiveLink(id) {
    for (const [target, link] of this.#links) {
      const isActive = target === id;
      link.classList.toggle('vanity-faq__nav-link--active', isActive);

      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Scrolls to the heading referenced by the page URL, e.g. when arriving from a shared link.
   */
  #scrollFromHash() {
    const hash = window.location.hash.slice(1);
    if (!hash || !this.#links.has(hash)) return;

    const anchor = this.querySelector(`#${CSS.escape(hash)}`);
    if (!(anchor instanceof HTMLElement)) return;

    this.#setActiveLink(hash);
    anchor.scrollIntoView({ block: 'start' });
  }
}

if (!customElements.get('vanity-faq')) {
  customElements.define('vanity-faq', VanityFaq);
}
